import { RippMark } from "../components/RippMark";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Radio, Zap, Waves, Target, ShieldAlert, Activity, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useAgentSocket } from "../hooks/useAgentSocket";
import { fmt } from "../lib/format";

/* Cinematic, LIVE-data product reel. 8s scripted beat loop over the real engine feed.
   Screen-record at 1080p for a video file. Nothing here places orders. */

const C = {
  void: "#04060A", cyan: "#00E5FF", teal: "#00E5B5", red: "#FF3355",
  acid: "#38BDF8", grey: "#8A94A6", white: "#E8EEF5",
};
const LOOP = 8000;
const BEAT = { tp: [1500, 4300], sweep: [3500, 5900], conf: [5100, 8000] };
const dec = (p) => (p == null ? 2 : p > 1000 ? 1 : p < 2 ? 4 : 2);
const ease = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
const win = (t, [a, b]) => (t >= a && t <= b ? Math.min(1, (t - a) / 380) * Math.min(1, (b - t) / 380) : 0);

/* ─────────── shells ─────────── */
const Card = ({ title, icon: Icon, right, children, testid, className = "" }) => (
  <div data-testid={testid}
    className={`rounded-xl border border-white/[0.07] bg-[#070A0F]/80 backdrop-blur-xl p-3 ${className}`}
    style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 10px 30px rgba(0,0,0,0.5)" }}>
    <div className="flex items-center justify-between mb-2.5">
      <span className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
        {Icon && <Icon size={10} strokeWidth={2} />}{title}
      </span>
      {right}
    </div>
    {children}
  </div>
);

const Row = ({ k, v, col }) => (
  <div className="flex items-baseline justify-between py-[3px]">
    <span className="text-[9.5px] font-mono uppercase tracking-[0.12em] text-zinc-500">{k}</span>
    <span className="text-[11.5px] font-bold font-mono tabular-nums" style={{ color: col || C.white }}>{v}</span>
  </div>
);

const Bar = ({ pct, col }) => (
  <div className="h-[3px] w-full rounded-full bg-white/[0.06] overflow-hidden">
    <div className="h-full rounded-full transition-[width] duration-700"
      style={{ width: `${Math.max(2, Math.min(100, pct))}%`, background: col, boxShadow: `0 0 8px ${col}` }} />
  </div>
);

/* ─────────── breathing risk ring ─────────── */
const RiskRing = ({ pct, t }) => {
  const r = 26, circ = 2 * Math.PI * r;
  const breathe = 1 + 0.03 * Math.sin(t / 420);
  const col = pct >= 80 ? C.red : pct >= 50 ? C.acid : C.teal;
  return (
    <div className="relative grid place-items-center" style={{ width: 68, height: 68, transform: `scale(${breathe})` }}>
      <svg width="68" height="68" className="-rotate-90">
        <circle cx="34" cy="34" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
        <circle cx="34" cy="34" r={r} fill="none" stroke={col} strokeWidth="5" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - Math.min(1, pct / 100))}
          style={{ transition: "stroke-dashoffset .9s ease", filter: `drop-shadow(0 0 7px ${col})` }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-[13px] font-black font-mono leading-none text-white tabular-nums">{Math.round(pct)}%</span>
        <span className="text-[6.5px] font-mono tracking-[0.14em] text-zinc-500">USED</span>
      </div>
    </div>
  );
};

/* ─────────── bottom strips ─────────── */
const MtfStrip = ({ ev, t }) => {
  const h4 = ev?.trend_htf?.bias || "NEUTRAL";
  const h1 = ev?.trend_ltf?.bias || "NEUTRAL";
  const tfs = [
    { k: "5M", b: ev?.structure?.trend || ev?.direction || "NEUTRAL" },
    { k: "15M", b: ev?.direction || ev?.structure?.trend || "NEUTRAL" },
    { k: "1H", b: h1 },
    { k: "4H", b: h4 },
    { k: "D", b: h4 },
  ];
  return (
    <div className="flex items-end justify-between gap-1.5 h-full">
      {tfs.map((x, i) => {
        const up = /LONG|BULL|UP/i.test(x.b || "");
        const dn = /SHORT|BEAR|DOWN/i.test(x.b || "");
        const col = up ? C.teal : dn ? C.red : C.grey;
        const p = 0.5 + 0.5 * Math.sin(t / 380 + i * 0.7);
        return (
          <div key={x.k} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full rounded-sm" style={{
              height: up || dn ? 22 : 10, background: `${col}${up || dn ? "33" : "1A"}`,
              borderTop: `2px solid ${col}`, opacity: 0.55 + 0.45 * p,
              boxShadow: up || dn ? `0 0 10px ${col}44` : "none",
            }} />
            <span className="text-[7.5px] font-mono tracking-[0.1em]" style={{ color: col }}>{x.k}</span>
          </div>
        );
      })}
    </div>
  );
};

const FlowStrip = ({ bars, t }) => {
  const cols = useMemo(() => (bars || []).slice(-26), [bars]);
  return (
    <div className="flex items-center gap-[2px] h-full">
      {cols.map((b, i) => {
        const up = b.c >= b.o;
        const rng = Math.max(1e-6, b.h - b.l);
        const h = 8 + Math.min(1, Math.abs(b.c - b.o) / rng) * 26;
        const shift = 0.75 + 0.25 * Math.sin(t / 300 + i * 0.55);
        return (
          <div key={i} className="flex-1 flex flex-col justify-center" style={{ height: "100%" }}>
            <div className="w-full rounded-[1px]" style={{
              height: h * shift, background: up ? C.teal : C.red, opacity: 0.35 + 0.5 * shift,
              alignSelf: up ? "flex-start" : "flex-end",
            }} />
          </div>
        );
      })}
    </div>
  );
};

const VolProfile = ({ bars }) => {
  const rows = useMemo(() => {
    const b = (bars || []).slice(-140);
    if (!b.length) return [];
    const lo = Math.min(...b.map((x) => x.l)), hi = Math.max(...b.map((x) => x.h));
    const N = 12, buckets = new Array(N).fill(0);
    b.forEach((x) => {
      const idx = Math.min(N - 1, Math.max(0, Math.floor(((x.c - lo) / Math.max(1e-9, hi - lo)) * N)));
      buckets[idx] += Math.max(1e-6, x.h - x.l);
    });
    const max = Math.max(...buckets, 1e-9);
    return buckets.map((v, i) => ({ pct: (v / max) * 100, poc: v === max, i }));
  }, [bars]);
  return (
    <div className="flex flex-col-reverse justify-between h-full gap-[2px]">
      {rows.map((r) => (
        <div key={r.i} className="h-[6px] rounded-[1px]" style={{
          width: `${Math.max(4, r.pct)}%`, background: r.poc ? C.acid : `${C.cyan}55`,
          boxShadow: r.poc ? `0 0 8px ${C.acid}66` : "none",
        }} />
      ))}
    </div>
  );
};

const ConfHistory = ({ signals, sym }) => {
  const real = useMemo(() => (signals || []).filter((s) => s.symbol === sym).slice(0, 24).reverse(), [signals, sym]);
  const pts = useMemo(() => [...new Array(Math.max(0, 24 - real.length)).fill({ score: 0 }), ...real], [real]);
  if (!real.length) return (
    <div className="grid h-full place-items-center">
      <span className="text-[8.5px] font-mono uppercase tracking-[0.2em] text-zinc-600">awaiting signals · {sym}</span>
    </div>
  );
  return (
    <div className="flex items-end gap-[3px] h-full">
      {pts.map((s, i) => {
        const v = Math.max(4, Math.min(100, ((s.score || 0) / 11) * 100));
        const good = (s.score || 0) >= 8;
        return <div key={i} className="flex-1 rounded-t-[1px]" style={{
          height: `${v}%`, background: good ? C.teal : `${C.grey}66`,
          boxShadow: good ? `0 0 8px ${C.teal}55` : "none",
        }} />;
      })}
    </div>
  );
};

/* ─────────── main page ─────────── */
export default function DemoReel() {
  const nav = useNavigate();
  const { state, connected } = useAgentSocket();
  const [sym, setSym] = useState("MNQ");
  const [bars, setBars] = useState([]);
  const [fc, setFc] = useState(null);
  const [t, setT] = useState(0);
  const canvasRef = useRef(null);
  const refs = useRef({});

  const market = state?.market || [];
  const m = market.find((x) => x.symbol === sym) || market[0] || {};
  const ev = state?.evaluations?.[sym];

  useEffect(() => {
    if (!market.length) return;
    // follow the symbol with the strongest live confluence — real market, whatever it's doing
    const best = [...market].sort((a, b) => (b.score || 0) - (a.score || 0))[0];
    if (best?.symbol && best.symbol !== sym && (best.score || 0) > (m.score || 0) + 1) setSym(best.symbol);
  }, [market.map((x) => `${x.symbol}${x.score}`).join("|")]); // eslint-disable-line

  useEffect(() => {
    let alive = true;
    const load = () => {
      api.chart(sym, "15m").then((r) => alive && setBars(r?.bars || [])).catch(() => {});
      api.forecast(sym, "15m", false).then((r) => alive && setFc(r?.forecast || null)).catch(() => {});
    };
    load();
    const id = setInterval(load, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [sym]);

  // loop clock
  useEffect(() => {
    let raf, start = performance.now();
    const tick = (now) => { setT((now - start) % LOOP); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  /* live setup geometry */
  const geo = useMemo(() => {
    const b = bars.slice(-90);
    if (b.length < 10) return null;
    const price = m.price ?? b[b.length - 1].c;
    const rng = b.reduce((a, x) => a + (x.h - x.l), 0) / b.length;
    const dir = (m.direction || (m.trend?.bias === "BEARISH" ? "SHORT" : "LONG")) === "SHORT" ? -1 : 1;
    const entry = m.entry ?? price;
    const tpFull = m.take_profit ?? entry + dir * rng * 6;
    const sl = m.stop_loss ?? entry - dir * rng * 2.2;
    const tps = [0.34, 0.67, 1].map((f, i) => ({ n: i + 1, p: entry + (tpFull - entry) * f }));
    const sweepLvl = m.sweep?.level ?? (dir > 0 ? Math.min(...b.slice(-24).map((x) => x.l)) : Math.max(...b.slice(-24).map((x) => x.h)));
    return { b, price, entry, sl, tps, tpFull, dir, sweepLvl, rng };
  }, [bars, m.price, m.entry, m.stop_loss, m.take_profit, m.direction, m.sweep?.level, m.trend?.bias]);

  refs.current = { geo, fc, t, dec: dec(geo?.price) };

  /* canvas: candles + liquid stream + levels + sweep ripple + scenario paths */
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    let raf;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const { geo: g, fc: f, t: tt } = refs.current;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const W = cv.clientWidth, H = cv.clientHeight;
      if (cv.width !== W * dpr || cv.height !== H * dpr) { cv.width = W * dpr; cv.height = H * dpr; }
      const x = cv.getContext("2d");
      x.setTransform(dpr, 0, 0, dpr, 0, 0);
      x.clearRect(0, 0, W, H);
      if (!g) return;

      const PAD = 14, RIGHT = W * 0.3;               // right band = projection
      const plotW = W - PAD * 2 - RIGHT;
      const lows = g.b.map((v) => v.l), highs = g.b.map((v) => v.h);
      let lo = Math.min(...lows, g.sl, g.sweepLvl), hi = Math.max(...highs, g.tpFull, g.sweepLvl);
      if (f?.scenarios) f.scenarios.forEach((s) => { lo = Math.min(lo, s.target); hi = Math.max(hi, s.target); });
      const pad = (hi - lo) * 0.12 || 1;
      lo -= pad; hi += pad;
      const Y = (p) => PAD + (1 - (p - lo) / (hi - lo)) * (H - PAD * 2);
      const X = (i) => PAD + (i / Math.max(1, g.b.length - 1)) * plotW;

      // grid
      x.strokeStyle = "rgba(255,255,255,0.035)"; x.lineWidth = 1;
      for (let i = 0; i <= 5; i++) { const yy = PAD + (i / 5) * (H - PAD * 2); x.beginPath(); x.moveTo(PAD, yy); x.lineTo(W - PAD, yy); x.stroke(); }

      // candles
      const cw = Math.max(1.5, plotW / g.b.length * 0.58);
      g.b.forEach((v, i) => {
        const up = v.c >= v.o;
        x.strokeStyle = up ? "rgba(0,229,181,0.75)" : "rgba(255,51,85,0.75)";
        x.fillStyle = up ? "rgba(0,229,181,0.22)" : "rgba(255,51,85,0.22)";
        x.lineWidth = 1;
        const xi = X(i);
        x.beginPath(); x.moveTo(xi, Y(v.h)); x.lineTo(xi, Y(v.l)); x.stroke();
        const y1 = Y(Math.max(v.o, v.c)), y2 = Y(Math.min(v.o, v.c));
        x.fillRect(xi - cw / 2, y1, cw, Math.max(1, y2 - y1));
        x.strokeRect(xi - cw / 2, y1, cw, Math.max(1, y2 - y1));
      });

      // liquid stream (smoothed closes) with flowing energy head
      const pts = g.b.map((v, i) => [X(i), Y(v.c)]);
      const stroke = (wid, alpha, blur) => {
        x.save(); x.beginPath(); x.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length - 1; i++) {
          const mx = (pts[i][0] + pts[i + 1][0]) / 2, my = (pts[i][1] + pts[i + 1][1]) / 2;
          x.quadraticCurveTo(pts[i][0], pts[i][1], mx, my);
        }
        x.strokeStyle = `rgba(0,229,255,${alpha})`; x.lineWidth = wid; x.lineJoin = "round";
        x.shadowColor = C.cyan; x.shadowBlur = blur; x.stroke(); x.restore();
      };
      stroke(9, 0.05, 26); stroke(4.5, 0.16, 16); stroke(1.7, 0.95, 10);

      // energy pulse travelling left -> right along the path
      const prog = (tt % LOOP) / LOOP;
      const head = Math.floor(prog * (pts.length - 1));
      const seg = pts.slice(Math.max(0, head - 9), head + 1);
      if (seg.length > 1) {
        x.save(); x.beginPath(); x.moveTo(seg[0][0], seg[0][1]);
        seg.slice(1).forEach((p) => x.lineTo(p[0], p[1]));
        x.strokeStyle = "rgba(255,255,255,0.9)"; x.lineWidth = 2.4;
        x.shadowColor = C.cyan; x.shadowBlur = 22; x.stroke();
        const hp = seg[seg.length - 1];
        const gr = x.createRadialGradient(hp[0], hp[1], 0, hp[0], hp[1], 16);
        gr.addColorStop(0, "rgba(255,255,255,0.85)"); gr.addColorStop(0.35, "rgba(0,229,255,0.45)"); gr.addColorStop(1, "rgba(0,229,255,0)");
        x.fillStyle = gr; x.beginPath(); x.arc(hp[0], hp[1], 16, 0, Math.PI * 2); x.fill(); x.restore();
      }

      const lastX = X(g.b.length - 1), lastY = Y(g.price);

      // entry / stop band
      x.fillStyle = "rgba(255,51,85,0.07)";
      x.fillRect(PAD, Math.min(Y(g.entry), Y(g.sl)), W - PAD * 2, Math.abs(Y(g.sl) - Y(g.entry)));
      const line = (p, col, label, dash, alpha = 1, weight = 1) => {
        x.save(); x.setLineDash(dash); x.strokeStyle = col; x.globalAlpha = alpha; x.lineWidth = weight;
        x.beginPath(); x.moveTo(PAD, Y(p)); x.lineTo(W - PAD, Y(p)); x.stroke();
        x.setLineDash([]); x.font = "600 9px ui-monospace, monospace"; x.fillStyle = col;
        x.fillText(label, PAD + 4, Y(p) - 4); x.restore();
      };
      line(g.entry, "rgba(232,238,245,0.55)", `ENTRY ${g.entry.toFixed(refs.current.dec)}`, [3, 4]);
      line(g.sl, "rgba(255,51,85,0.8)", `STOP ${g.sl.toFixed(refs.current.dec)}`, [5, 4]);

      // TP1/2/3 — pulse harder as price closes in
      g.tps.forEach((tp) => {
        const dist = Math.abs(tp.p - g.price), reach = Math.abs(g.tpFull - g.entry) || 1;
        const near = Math.max(0, 1 - dist / (reach * 0.5));
        const puls = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(tt / (260 - near * 120)));
        line(tp.p, `rgba(0,229,181,${Math.min(1, (0.55 + 0.45 * near) * puls)})`,
          `TP${tp.n} ${tp.p.toFixed(refs.current.dec)}`, [7, 5], 1, 1 + near);
      });

      // liquidity sweep ripple
      const sw = win(tt, BEAT.sweep);
      if (sw > 0) {
        const sy = Y(g.sweepLvl), sx = lastX - plotW * 0.12;
        for (let k = 0; k < 3; k++) {
          const ph = ((tt - BEAT.sweep[0]) / 700 + k * 0.33) % 1;
          x.save(); x.globalAlpha = sw * (1 - ph) * 0.8;
          x.strokeStyle = C.acid; x.lineWidth = 1.6; x.shadowColor = C.acid; x.shadowBlur = 14;
          x.beginPath(); x.ellipse(sx, sy, 18 + ph * 90, 6 + ph * 26, 0, 0, Math.PI * 2); x.stroke(); x.restore();
        }
        x.save(); x.globalAlpha = sw; x.font = "700 10px ui-monospace, monospace"; x.fillStyle = C.acid;
        x.shadowColor = C.acid; x.shadowBlur = 10;
        x.fillText("LIQUIDITY SWEEP DETECTED", sx - 60, sy + 22); x.restore();
      }

      // 3 shimmering scenario paths with fading uncertainty
      const scen = f?.scenarios || [];
      const colOf = (ty) => (ty === "BULL" ? C.teal : ty === "BEAR" ? C.red : C.grey);
      scen.forEach((s, si) => {
        const steps = 34, x0 = lastX, y0 = lastY, x1 = W - PAD, y1 = Y(s.target);
        x.save();
        x.setLineDash([6, 6]); x.lineDashOffset = -(tt / 26) - si * 4;
        x.strokeStyle = colOf(s.type); x.globalAlpha = 0.55; x.lineWidth = 1.4;
        x.shadowColor = colOf(s.type); x.shadowBlur = 8;
        x.beginPath();
        for (let i = 0; i <= steps; i++) {
          const p = i / steps, xx = x0 + (x1 - x0) * p;
          const wob = Math.sin(p * 6 + tt / 500 + si) * (1 - p) * g.rng * 0.25;
          const yy = y0 + (y1 - y0) * ease(p) + (Y(g.price + wob) - Y(g.price));
          i ? x.lineTo(xx, yy) : x.moveTo(xx, yy);
        }
        x.stroke();
        x.setLineDash([]); x.globalAlpha = 0.75; x.font = "700 8.5px ui-monospace, monospace";
        x.fillStyle = colOf(s.type);
        x.fillText(`${s.type} ${s.prob}%  ${s.target.toFixed(refs.current.dec)}`,
          s.type === "BASE" ? x1 - 210 : x1 - 96, y1 - 6);
        x.restore();
      });
      if (scen.length >= 2) {
        const bull = scen.find((s) => s.type === "BULL"), bear = scen.find((s) => s.type === "BEAR");
        if (bull && bear) {
          const gr = x.createLinearGradient(lastX, 0, W - PAD, 0);
          gr.addColorStop(0, "rgba(0,229,255,0.10)"); gr.addColorStop(1, "rgba(0,229,255,0)");
          x.fillStyle = gr; x.beginPath();
          x.moveTo(lastX, lastY); x.lineTo(W - PAD, Y(bull.target)); x.lineTo(W - PAD, Y(bear.target)); x.closePath(); x.fill();
        }
      }

      // now line + live price tag
      x.save(); x.setLineDash([2, 5]); x.strokeStyle = "rgba(56,189,248,0.5)"; x.lineWidth = 1;
      x.beginPath(); x.moveTo(lastX, PAD); x.lineTo(lastX, H - PAD); x.stroke(); x.restore();
      x.fillStyle = C.acid; x.font = "700 9px ui-monospace, monospace"; x.fillText("NOW", lastX + 4, PAD + 9);
      const tag = g.price.toFixed(refs.current.dec);
      x.save(); x.fillStyle = "rgba(0,229,255,0.16)"; x.strokeStyle = C.cyan; x.lineWidth = 1;
      x.beginPath(); x.roundRect(W - PAD - 78, lastY - 9, 74, 18, 4); x.fill(); x.stroke();
      x.fillStyle = C.cyan; x.font = "700 10px ui-monospace, monospace"; x.fillText(tag, W - PAD - 70, lastY + 4); x.restore();
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  /* beats */
  const tpA = win(t, BEAT.tp), swA = win(t, BEAT.sweep), cfA = win(t, BEAT.conf);
  const tp1 = geo?.tps?.[0];
  const ptsAway = tp1 && geo ? Math.abs(tp1.p - geo.price) : 0;
  const score = m.score || 0;
  const conf100 = Math.min(100, Math.round((score / 11) * 100));
  const acct = state?.account || {};
  const cfgRisk = state?.config || {};
  const dayLimit = (acct.day_start_equity || acct.balance || 1) * ((cfgRisk.max_daily_loss ?? 0.03));
  const riskUsed = Math.min(100, Math.abs(Math.min(0, acct.daily_pnl || 0)) / Math.max(1e-6, dayLimit) * 100);
  const prog = t / LOOP;
  // slow 2.5% push-in, eased back at the tail so the loop is seamless
  const zoom = 1 + 0.025 * (prog < 0.88 ? ease(prog / 0.88) : 1 - ease((prog - 0.88) / 0.12));
  const primary = fc?.scenarios ? [...fc.scenarios].sort((a, b) => b.prob - a.prob)[0] : null;
  const nextClose = useMemo(() => {
    const now = Date.now() / 1000, s = 900;
    return Math.max(0, Math.round(s - (now % s)));
  }, [Math.floor(Date.now() / 1000)]);

  return (
    <div className="min-h-screen w-full overflow-hidden text-zinc-200 font-sans relative"
      style={{ background: C.void }} data-testid="demo-reel">
      {/* grain + vignette */}
      <div className="pointer-events-none fixed inset-0 z-50 opacity-[0.05] mix-blend-overlay"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
      <div className="pointer-events-none fixed inset-0 z-40"
        style={{ background: "radial-gradient(120% 90% at 50% 40%, transparent 45%, rgba(0,0,0,0.65) 100%)" }} />

      <button onClick={() => nav("/v2")} data-testid="demo-back"
        className="fixed left-4 bottom-4 z-[60] flex items-center gap-1.5 rounded-full border border-white/10 bg-black/60 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.16em] text-zinc-400 backdrop-blur hover:text-white transition-colors">
        <ArrowLeft size={12} /> Terminal
      </button>

      <div className="relative z-10 h-screen p-5 will-change-transform"
        style={{ transform: `scale(${zoom})`, transformOrigin: "52% 46%" }}>
        {/* header */}
        <div className="flex items-center justify-between mb-3" style={{ transform: `translateY(${-prog * 4}px)` }}>
          <div className="flex items-center gap-3">
            <RippMark size={34} className="text-[#38BDF8]" glow />
            <div>
              <div className="text-[15px] font-black tracking-[0.16em] text-white leading-none">RIPP LABS</div>
              <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-1">
                Build your strategy. Let it RIPP. <span className="text-[#38BDF8]">ripp.xyz</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-[13px] font-bold font-mono text-white" data-testid="demo-instrument">
                {m.symbol}1! · {m.name || "FUTURES"}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                15M · {m.source || "LIVE"} · {(m.trend?.bias || "NEUTRAL")}
              </div>
            </div>
            <span className="flex items-center gap-1.5 rounded-full border border-white/10 bg-black/50 px-2.5 py-1 text-[9px] font-mono uppercase tracking-[0.16em]"
              style={{ color: connected ? C.teal : C.grey }} data-testid="demo-live-dot">
              <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: connected ? C.teal : C.grey }} />
              {connected ? "LIVE" : "SYNC"}
            </span>
          </div>
        </div>

        {/* chart + rail */}
        <div className="flex gap-3" style={{ height: "62%" }}>
          <div className="relative flex-1 rounded-xl border border-white/[0.07] bg-[#060910]/70 overflow-hidden"
            style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 20px 60px rgba(0,0,0,0.6)" }}>
            <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" data-testid="demo-canvas" />

            {/* beat 1 — TP1 approaching */}
            <div className="absolute left-1/2 top-5 -translate-x-1/2 pointer-events-none" data-testid="demo-tp-toast"
              style={{ opacity: tpA, transform: `translate(-50%, ${(1 - tpA) * -10}px)` }}>
              <div className="flex items-center gap-2 rounded-lg border px-3 py-2 backdrop-blur-xl"
                style={{ borderColor: `${C.teal}55`, background: "rgba(0,20,18,0.72)", boxShadow: `0 0 26px ${C.teal}33` }}>
                <Target size={13} style={{ color: C.teal }} />
                <span className="text-[11px] font-bold font-mono tracking-[0.1em]" style={{ color: C.teal }}>
                  TP1 APPROACHING — {ptsAway.toFixed(2)} PTS
                </span>
                <div className="w-20"><Bar pct={tp1 && geo ? 100 - Math.min(100, (ptsAway / Math.max(1e-6, Math.abs(geo.tpFull - geo.entry))) * 100) : 0} col={C.teal} /></div>
              </div>
            </div>

            {/* beat 2 — sweep banner */}
            <div className="absolute left-5 bottom-5 pointer-events-none" data-testid="demo-sweep-banner"
              style={{ opacity: swA, transform: `translateX(${(1 - swA) * -12}px)` }}>
              <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5 backdrop-blur-xl"
                style={{ borderColor: `${C.acid}55`, background: "rgba(18,20,0,0.7)", boxShadow: `0 0 26px ${C.acid}22` }}>
                <Waves size={12} style={{ color: C.acid }} />
                <span className="text-[10px] font-bold font-mono tracking-[0.14em]" style={{ color: C.acid }}>
                  LIQUIDITY SWEEP DETECTED · {m.direction === "SHORT" ? "BUY" : "SELL"} STOP RUN
                </span>
              </div>
            </div>

            <div className="absolute right-4 top-4 flex gap-1.5 pointer-events-none">
              {["ZONES", "FVG", "SWEEP", "MSS"].map((k, i) => (
                <span key={k} className="rounded-sm border px-1.5 py-0.5 text-[8px] font-mono tracking-[0.14em]"
                  style={{
                    borderColor: "rgba(255,255,255,0.08)", color: C.grey,
                    background: `rgba(0,229,255,${0.04 + 0.05 * (0.5 + 0.5 * Math.sin(t / 400 + i))})`,
                  }}>{k}</span>
              ))}
            </div>
          </div>

          {/* intelligence rail */}
          <div className="w-[290px] flex-shrink-0 flex flex-col gap-2.5" style={{ transform: `translateX(${prog * 3}px)` }}>
            {/* beat 3 — confluence card slides in */}
            <div data-testid="demo-confluence-card" style={{ opacity: 0.35 + 0.65 * cfA, transform: `translateX(${(1 - cfA) * 16}px)` }}>
              <Card title="Confluence Engine" icon={Zap} testid="demo-conf-inner"
                right={<span className="text-[9px] font-mono" style={{ color: C.acid }}>{m.grade || "—"}</span>}>
                <div className="flex items-end justify-between">
                  <span className="text-[26px] font-black font-mono leading-none tabular-nums" style={{ color: conf100 >= 72 ? C.teal : C.acid }}>
                    {conf100}<span className="text-[13px] text-zinc-600">/100</span>
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500">{score} HITS · MIN {cfgRisk.confluence_threshold ?? 8}</span>
                </div>
                <div className="mt-2"><Bar pct={conf100} col={conf100 >= 72 ? C.teal : C.acid} /></div>
              </Card>
            </div>

            <Card title="Target Tracker" icon={Target} testid="demo-target-tracker">
              <Row k="Active Setup" v={`${m.direction || "—"} ${m.structure?.event || m.structure?.trend || ""}`} col={m.direction === "SHORT" ? C.red : C.teal} />
              <Row k="Probability" v={primary ? `${primary.prob}% ${primary.type}` : "—"} col={C.cyan} />
              <Row k="Confidence" v={fc ? (fc.confidence >= 0.66 ? "HIGH" : fc.confidence >= 0.4 ? "MEDIUM" : "LOW") : "—"} />
              <Row k="Risk / Reward" v={m.rr ? `${Number(m.rr).toFixed(2)}R` : "—"} col={(m.rr || 0) >= 1.5 ? C.teal : C.red} />
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[8px] font-mono tracking-[0.14em] text-zinc-600">PROGRESS</span>
                <div className="flex-1"><Bar pct={geo ? 100 - Math.min(100, (ptsAway / Math.max(1e-6, Math.abs(geo.tpFull - geo.entry))) * 100) : 0} col={C.cyan} /></div>
              </div>
            </Card>

            <Card title="Next Candle Close" icon={Clock} testid="demo-next-event">
              <div className="flex items-baseline gap-2">
                <span className="text-[22px] font-black font-mono tabular-nums text-white">
                  {String(Math.floor(nextClose / 60)).padStart(2, "0")}:{String(nextClose % 60).padStart(2, "0")}
                </span>
                <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-zinc-500">
                  {state?.sessions?.[sym]?.open ? "SESSION OPEN" : "SESSION CLOSED"}
                </span>
              </div>
            </Card>

            <Card title="Risk Controls" icon={ShieldAlert} testid="demo-risk-controls" className="flex-1">
              <div className="flex items-center gap-3">
                <RiskRing pct={riskUsed} t={t} />
                <div className="flex-1">
                  <Row k="Daily Max Loss" v={`${((cfgRisk.max_daily_loss ?? 0) * 100).toFixed(0)}%`} />
                  <Row k="Risk / Trade" v={`${((cfgRisk.risk_per_trade ?? 0) * 100).toFixed(0)}%`} />
                  <Row k="Sizing" v={cfgRisk.contract_mode || "MICRO"} col={C.acid} />
                  <Row k="Unrealized" v={`$${fmt(acct.unrealized_pnl ?? 0, 2)}`} col={(acct.unrealized_pnl ?? 0) >= 0 ? C.teal : C.red} />
                </div>
              </div>
              <div className="mt-3 pt-2.5 border-t border-white/[0.06]">
                <Row k="Equity" v={`$${fmt(acct.equity ?? 0, 2)}`} />
                <Row k="Net P/L (day)" v={`$${fmt(acct.daily_pnl ?? 0, 2)}`} col={(acct.daily_pnl ?? 0) >= 0 ? C.teal : C.red} />
                <Row k="Open Positions" v={`${(state?.positions || []).length}`} col={C.cyan} />
                <Row k="Win Rate" v={`${fmt(acct.winrate ?? 0, 1)}%`} />
                <Row k="Agent" v={cfgRisk.trust_mode && cfgRisk.running ? "ARMED · AUTO" : cfgRisk.running ? "HUNTING" : "STANDBY"}
                  col={cfgRisk.trust_mode && cfgRisk.running ? C.teal : C.acid} />
              </div>
            </Card>
          </div>
        </div>

        {/* bottom analytics row */}
        <div className="grid grid-cols-6 gap-2.5 mt-3" style={{ height: "24%", transform: `translateY(${prog * 3}px)` }}>
          <Card title="MTF Alignment" icon={Activity} testid="demo-mtf" className="flex flex-col">
            <div className="flex-1 min-h-0"><MtfStrip ev={ev} t={t} /></div>
          </Card>
          <Card title="Order Flow" icon={Activity} testid="demo-flow" className="flex flex-col">
            <div className="flex-1 min-h-0"><FlowStrip bars={bars} t={t} /></div>
          </Card>
          <Card title="Volume Profile" testid="demo-volprofile" className="flex flex-col">
            <div className="flex-1 min-h-0"><VolProfile bars={bars} /></div>
          </Card>
          <Card title="Session Range" testid="demo-session" className="flex flex-col justify-center">
            {geo && (
              <>
                <div className="flex justify-between text-[9px] font-mono text-zinc-500">
                  <span>{fmt(Math.min(...geo.b.map((x) => x.l)), dec(geo.price))}</span>
                  <span>{fmt(Math.max(...geo.b.map((x) => x.h)), dec(geo.price))}</span>
                </div>
                <div className="relative mt-2 h-[3px] w-full rounded-full bg-white/[0.06]">
                  {(() => {
                    const lo = Math.min(...geo.b.map((x) => x.l)), hi = Math.max(...geo.b.map((x) => x.h));
                    const p = ((geo.price - lo) / Math.max(1e-9, hi - lo)) * 100;
                    return <div className="absolute -top-[3px] h-[9px] w-[9px] rounded-full"
                      style={{ left: `calc(${Math.max(0, Math.min(100, p))}% - 4px)`, background: C.cyan, boxShadow: `0 0 10px ${C.cyan}` }} />;
                  })()}
                </div>
                <div className="mt-2 text-[10px] font-mono text-white tabular-nums">{fmt(geo.price, dec(geo.price))}</div>
              </>
            )}
          </Card>
          <Card title="Confluence History" testid="demo-confhist" className="flex flex-col">
            <div className="flex-1 min-h-0"><ConfHistory signals={state?.signals} sym={sym} /></div>
          </Card>
          <Card title="3-Path Scenario" icon={Radio} testid="demo-scenarios" className="flex flex-col justify-center gap-1.5">
            {(fc?.scenarios || []).map((s) => (
              <div key={s.type}>
                <div className="flex justify-between text-[9px] font-mono">
                  <span style={{ color: s.type === "BULL" ? C.teal : s.type === "BEAR" ? C.red : C.grey }}>{s.type}</span>
                  <span className="text-zinc-400 tabular-nums">{s.prob}%</span>
                </div>
                <Bar pct={s.prob} col={s.type === "BULL" ? C.teal : s.type === "BEAR" ? C.red : C.grey} />
              </div>
            ))}
            {!fc && <span className="text-[9px] font-mono text-zinc-600">awaiting forecast…</span>}
          </Card>
        </div>
      </div>
    </div>
  );
}
